import { supabase } from './supabase'

// Fetch nomination requests with optional filters
export async function getNominationRequests(filters = {}) {
  let query = supabase
    .from('nomination_requests')
    .select('*')
    .order('created_at', { ascending: false })

  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status)
  }

  if (filters.searchTerm) {
    const term = `%${filters.searchTerm}%`
    query = query.or(`first_name.ilike.${term},last_name.ilike.${term},email.ilike.${term},phone.ilike.${term},surgery_name.ilike.${term},postcode.ilike.${term}`)
  }

  if (filters.dateFrom) {
    query = query.gte('created_at', filters.dateFrom)
  }

  if (filters.dateTo) {
    query = query.lte('created_at', filters.dateTo)
  }

  return await query
}

// Fetch service requests with optional filters
export async function getServiceRequests(filters = {}) {
  let query = supabase
    .from('service_requests')
    .select('*')
    .order('created_at', { ascending: false })

  if (filters.status && filters.status !== 'all') {
    query = query.eq('status', filters.status)
  }

  if (filters.searchTerm) {
    const term = `%${filters.searchTerm}%`
    query = query.or(`first_name.ilike.${term},last_name.ilike.${term},email.ilike.${term},phone.ilike.${term}`)
  }

  if (filters.dateFrom) {
    query = query.gte('created_at', filters.dateFrom)
  }

  if (filters.dateTo) {
    query = query.lte('created_at', filters.dateTo)
  }

  return await query
}

// Update request status
export async function updateRequestStatus(table, id, newStatus) {
  return await supabase
    .from(table)
    .update({ status: newStatus, updated_at: new Date().toISOString() })
    .eq('id', id)
}

// Get request counts by status
// export async function getRequestCounts(table) {
//   const { data, error } = await supabase
//     .from(table)
//     .select('status')

//   if (error) throw error

//   const counts = {
//     total: data.length,
//     pending: 0,
//     ready: 0,
//     completed: 0,
//     cancelled: 0,
//     confirmed: 0,
//     rejected: 0,
//   }

//   data.forEach(item => {
//     if (counts.hasOwnProperty(item.status)) {
//       counts[item.status]++
//     }
//   })

//   return counts
// }

// Get single request by ID
export async function getRequestById(table, id) {
  return await supabase
    .from(table)
    .select('*')
    .eq('id', id)
    .single()
}

// ============ Admin User Management ============

// Get all admin users
export async function getAdminUsers() {
  return await supabase
    .from('admin_users')
    .select('*')
    .order('created_at', { ascending: false })
}

// Add a new admin user
export async function addAdminUser(email, addedBy) {
  return await supabase
    .from('admin_users')
    .insert([{
      email: email.toLowerCase().trim(),
      added_by: addedBy
    }])
    .select()
    .single()
}

// Remove an admin user
export async function removeAdminUser(id) {
  return await supabase
    .from('admin_users')
    .delete()
    .eq('id', id)
}

// Check if a user is an admin
export async function isUserAdmin(email) {
  const { data, error } = await supabase
    .from('admin_users')
    .select('id')
    .eq('email', email.toLowerCase().trim())
    .single()

  if (error && error.code !== 'PGRST116') {
    // PGRST116 is "no rows found" which is expected if not admin
    throw error
  }

  return !!data
}

// Check if a user is a super admin
export async function checkSuperAdmin(email) {
  const { data, error } = await supabase
    .from('admin_users')
    .select('is_super_admin')
    .eq('email', email.toLowerCase().trim())
    .single()

  if (error && error.code !== 'PGRST116') {
    throw error
  }

  return data?.is_super_admin || false
}

// ============ Patient Management ============

// Get patients with optional filters (calls RPC function)
export async function getPatients(filters = {}) {
  const { data, error } = await supabase.rpc('get_patients', {
    p_min_age: filters.minAge || null,
    p_max_age: filters.maxAge || null,
    p_service_filter: filters.service || null
  })

  return { data, error }
}

// Add a manual patient entry
export async function addManualPatient(patient) {
  return await supabase
    .from('manual_patients')
    .insert([{
      first_name: patient.firstName.trim(),
      last_name: patient.lastName.trim(),
      email: patient.email.toLowerCase().trim(),
      date_of_birth: patient.dateOfBirth
    }])
    .select()
    .single()
}

// Send bulk email to patients
export async function sendBulkEmail(recipientEmails, subject, body) {
  return await supabase.functions.invoke('send-notification-email', {
    body: {
      type: 'bulk_patient_email',
      data: {
        email: recipientEmails[0], // Required by function signature
        recipient_emails: recipientEmails,
        subject,
        body
      }
    }
  })
}
